/**
 * Tallies the total number of wins and places for all tips for a given date.
 * Each tip position (tip1, tip2, tip3, tip4) is tracked separately.
 * Also tracks wins and places by track and race.
 *
 * @param {Object} historicalData - The historical results data object
 * @param {number} tipCount - Number of tips to count (default: 2)
 * @returns {Object} Object containing wins and places counts for each tip position, track, and race
 */
export const tallyResultsForToday = (historicalData, tipCount = 2) => {
  // Initialize the result object
  const tally = {
    tip1: { wins: 0, places: 0 },
    tip2: { wins: 0, places: 0 },
    tip3: { wins: 0, places: 0 },
    tip4: { wins: 0, places: 0 },
    total: { wins: 0, places: 0 },
    byTrack: {},
    byRace: {},
    tracksWithResults: new Set(), // Tracks that have actual results
    racesWithResults: new Set(), // Race keys that have actual results
  };

  // Ensure tipCount is valid (between 1 and 4)
  tipCount = Math.max(1, Math.min(4, tipCount));

  // If no historical data, return empty results
  if (!historicalData || Object.keys(historicalData).length === 0) {
    return tally;
  }

  // Process each race in the historical data
  Object.entries(historicalData).forEach(([key, raceData]) => {
    // Skip if no podium data
    if (!raceData.podium || !Array.isArray(raceData.podium)) {
      return;
    }

    // Extract track and race number from key
    const parts = key.split("-");
    const raceNumber = parts[parts.length - 4];
    const trackName = parts.slice(0, parts.length - 4).join(" ");

    // Check if race has actual results (at least one win or place)
    let hasActualResults = false;

    // Initialize race data
    const raceWins = 0;
    const racePlaces = 0;

    // Count wins and places for each tip position (up to tipCount)
    raceData.podium.slice(0, tipCount).forEach((result, index) => {
      if (index >= 4) return; // Only count first 4 positions as maximum

      const tipPosition = `tip${index + 1}`;

      if (result === "winnerWinnerChickenDinner") {
        tally[tipPosition].wins++;
        tally.total.wins++;
        hasActualResults = true;

        // Initialize track data if needed
        if (!tally.byTrack[trackName]) {
          tally.byTrack[trackName] = { wins: 0, places: 0, hasResults: true };
        }
        tally.byTrack[trackName].wins++;

        // Initialize race data if needed
        if (!tally.byRace[key]) {
          tally.byRace[key] = {
            wins: 0,
            places: 0,
            trackName,
            raceNumber,
            hasResults: true,
          };
        }
        tally.byRace[key].wins++;
      } else if (result === "PL") {
        tally[tipPosition].places++;
        tally.total.places++;
        hasActualResults = true;

        // Initialize track data if needed
        if (!tally.byTrack[trackName]) {
          tally.byTrack[trackName] = { wins: 0, places: 0, hasResults: true };
        }
        tally.byTrack[trackName].places++;

        // Initialize race data if needed
        if (!tally.byRace[key]) {
          tally.byRace[key] = {
            wins: 0,
            places: 0,
            trackName,
            raceNumber,
            hasResults: true,
          };
        }
        tally.byRace[key].places++;
      }
    });

    // Mark this track and race as having results if there was at least one win or place
    if (hasActualResults) {
      tally.tracksWithResults.add(trackName);
      tally.racesWithResults.add(key);
    }
  });

  return tally;
};

// Detect if a race has results (Betfair winner or placegetters)
export const hasRaceResults = (trackName, raceNumber, raceDate, historicalData) => {
  if (!trackName || !raceNumber || !raceDate || !historicalData) {
    return false;
  }

  const raceKey = `${trackName.replace(/\s+/g, "-")}-${raceNumber}-${raceDate}`;
  const raceResults = historicalData[raceKey];

  return !!(
    raceResults?.betfairResults &&
    (raceResults.betfairResults.winner ||
      (raceResults.betfairResults.placeGetters &&
        raceResults.betfairResults.placeGetters.length > 0))
  );
};
